/**
 * @file BinsList.tsx
 * @description Сетка карточек ячеек и её состояния (phase19.23, A4).
 *
 * Вынесено из страницы: «склад без полок», ошибка, скелет, пустой результат
 * поиска и пустой справочник — пять состояний одного блока, и держать их
 * вперемешку с диалогами страницы нельзя (SRP).
 *
 * phase19.22 шаг 5: на `<lg` — карточки, на `≥lg` — таблица (`BinsTable`);
 * при праве печати у активных ячеек галочки под этикетки.
 *
 * phase19.31 (SC69): таблица и пустые состояния — на непрозрачной поверхности.
 */

import { Boxes, Grid3x3, Loader2, Search } from "lucide-react";

import { BinCard } from "./BinCard";
import { BinsTable } from "./BinsTable";
import type { BinSelection } from "./useBinSelection";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { EmptyState } from "../../ui/EmptyState";
import { LIST_FRAME_CLASS, LIST_SURFACE_CLASS } from "../../ui/ListStates";
import type { StorageBinDto } from "../../../lib/api-bins";
import { cn } from "@/lib/utils";

interface Props {
  bins: StorageBinDto[];
  loading: boolean;
  error: string | null;
  /** true — список пуст из-за поиска, а не потому что ячеек нет. */
  searching: boolean;
  /** Склад без полок (транзит, ПВЗ) — ячеек у него не бывает. */
  unsupportedKindLabel: string | null;
  generateDisabled: boolean;
  onGenerate: () => void;
  onOpen: (bin: StorageBinDto) => void;
  onPut: (bin: StorageBinDto) => void;
  onArchive: (bin: StorageBinDto) => void;
  onRestore: (bin: StorageBinDto) => void;
  /** Отметки под печать этикеток; `null` — печать недоступна. */
  selection: BinSelection | null;
}

export function BinsList({
  bins,
  loading,
  error,
  searching,
  unsupportedKindLabel,
  generateDisabled,
  onGenerate,
  onOpen,
  onPut,
  onArchive,
  onRestore,
  selection,
}: Props) {
  if (unsupportedKindLabel) {
    return (
      <Alert severity="info">
        У склада типа «{unsupportedKindLabel}» ячеек не бывает — адресное
        хранение доступно складам с полками.
      </Alert>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="size-5 animate-spin text-primary" />
      </div>
    );
  }

  if (bins.length === 0) {
    return searching ? (
      <EmptyState
        className={LIST_SURFACE_CLASS}
        icon={Search}
        variant="filtered"
        title="Такого адреса на складе нет"
        description="Проверьте формат адреса (A-02-03) или сбросьте поиск."
      />
    ) : (
      <EmptyState
        className={LIST_SURFACE_CLASS}
        icon={Boxes}
        title="Ячеек на этом складе нет"
        description="Заведите одну или сгенерируйте стеллаж целиком — адреса нужны для маршрута обхода при сборке."
        action={
          <Button onClick={onGenerate} disabled={generateDisabled}>
            <Grid3x3 className="size-4" />
            <span className="ml-1.5">Сгенерировать стеллаж</span>
          </Button>
        }
      />
    );
  }

  return (
    <>
      <ul className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:hidden">
        {bins.map((bin) => (
          <BinCard
            key={bin.id}
            bin={bin}
            onOpen={() => onOpen(bin)}
            onPut={() => onPut(bin)}
            onArchive={() => onArchive(bin)}
            onRestore={() => onRestore(bin)}
            selected={selection?.isSelected(bin.id) ?? false}
            onToggleSelect={selection ? () => selection.toggle(bin.id) : undefined}
          />
        ))}
      </ul>
      {/* SC69: таблица ячеек — на непрозрачной поверхности в общей рамке. */}
      <div className={cn("hidden lg:block", LIST_FRAME_CLASS)}>
        <BinsTable
          bins={bins}
          selection={selection}
          onOpen={onOpen}
          onPut={onPut}
          onArchive={onArchive}
          onRestore={onRestore}
        />
      </div>
    </>
  );
}
